import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { Box, Button, Divider, FormControl, Typography } from "@mui/material";

import fontWeight from "../../styles/mui/fontWeight";
import { colors } from "../../styles/colors";
import { FormInputText } from "../../components/FormInputText";
import { TextfieldStyle } from "../../routes/Training/styles/style";
import {
  DisplayItemCenter,
  HorizontalDivider,
  UserRegistrationBox,
} from "../styles/styles";
import { Asterisk } from "../../styles/common/style";
import { rules } from "../../utils/validation/Validation";
import { RegisterUser, UserByClaims } from "../../models/User";
import { getValue, Logout } from "../../utils";
import { CONFIRM_PASSWORD_ERROR_MESSAGE } from "../../constants/User";
import { isEmpty } from "lodash";
import { Numbers } from "../../constants/Referral";
import { redirectUri } from "../../constants/Config";
import { useMsal } from "@azure/msal-react";
import {
  LoginCircle1,
  LoginCircle2,
  LoginCircle3,
  LoginCircle4,
  LoginCircle5,
  Min_Vh_100,
} from "../../loginResources/backgroundStyle";

export interface PropsFromState {
  registerUserState: RegisterUser;
  userByClaimsState: UserByClaims;
  registerUserStateError: any;
}
export interface PropsFromDispatch {
  postRegisterUser: (payload: RegisterUser, headers: any) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const UserRegistration: React.FC<AllProps> = ({
  postRegisterUser,
  userByClaimsState,
  registerUserState,
  registerUserStateError
}) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const code = encodeURIComponent(urlParams.get("code") ?? "");
  const { instance } = useMsal();

  const methods = useForm<RegisterUser>({
    defaultValues: {
      portalUserIdToken: getValue(userByClaimsState, "identifier"),
      displayName: `${getValue(userByClaimsState, "firstName")} ${getValue(
        userByClaimsState,
        "lastName"
      )}`,
      email: getValue(userByClaimsState, "email"),
      password: "",
      confirmPassword: "",
    },
  });

  const { control, handleSubmit, setError } = methods;

  const onSubmit = (data: RegisterUser) => {
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: CONFIRM_PASSWORD_ERROR_MESSAGE,
      });
      return;
    }
    const payload = {
      portalUserIdToken: data.portalUserIdToken,
      displayName: data.displayName,
      email: data.email,
      password: data.password,
    };
    const headers = {
      "x-portl-claim-key": code,
    };
    postRegisterUser(payload, headers);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (
      !isEmpty(registerUserState.password) &&
      registerUserState.email.length > Numbers.ZERO
    ) {
      Logout(redirectUri, instance);
    }
  }, [registerUserState]);

  useEffect(() => {
    if (registerUserStateError) {
      Logout(redirectUri, instance);
    }
  },[registerUserStateError])

  const label = (label: string) => {
    return (
      <Grid size={{ xs: 12 }} sx={DisplayItemCenter(false)}>
        <Typography
          variant="h3"
          color={colors.portalColor[2]}
          fontWeight={fontWeight[5]}
        >
          {label}
        </Typography>
        <Typography
          variant="subtitle1"
          color={colors.red[90]}
          fontWeight={fontWeight[4]}
          pr={2}
          sx={Asterisk}
        >
          *
        </Typography>
      </Grid>
    );
  };

  return (
    <Box sx={Min_Vh_100}>
      <Box sx={LoginCircle1}></Box>
      <Box sx={LoginCircle2}></Box>
      <Box sx={LoginCircle3}></Box>
      <Box sx={LoginCircle4}></Box>
      <Box sx={LoginCircle5}></Box>
      <Grid container>
        <Grid size={{ xs: 3 }} mt={3} />
        <Grid size={{ xs: 6 }} mt={5} sx={UserRegistrationBox}>
          <Grid container >
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h1"
                fontWeight={fontWeight[3]}
                color={colors.portalColor[2]}
                fontSize={"1.5rem"}
                align="center"
                pb={1}
              >
                User Registration
              </Typography>
              <Divider sx={HorizontalDivider} />
            </Grid>
            <Grid size={{ xs: 12 }} pt={2}>
              {label("Name")}
              <FormControl variant="standard" fullWidth>
                <FormInputText
                  name="displayName"
                  control={control}
                  style={TextfieldStyle}
                  disabled={true}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pt={2}>
              {label("Email")}
              <FormControl variant="standard" fullWidth>
                <FormInputText
                  name="email"
                  control={control}
                  style={TextfieldStyle}
                  disabled={true}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pt={2}>
              {label("Password")}
              <FormControl variant="standard" fullWidth>
                <FormInputText
                  name="password"
                  control={control}
                  inputType="password"
                  style={TextfieldStyle}
                  helper={rules.password}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pt={2}>
              {label("Confirm password")}
              <FormControl variant="standard" fullWidth>
                <FormInputText
                  name="confirmPassword"
                  control={control}
                  inputType="password"
                  style={TextfieldStyle}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }} pt={2} sx={DisplayItemCenter(false)}>
              <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 3 }} />
      </Grid>
    </Box>
  );
};

export default UserRegistration;
